import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { ordersAPI, returnsAPI, getErrorMessage } from "../services/api";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import toast from "react-hot-toast";
import { format } from "date-fns";

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  paid: "bg-blue-100 text-blue-800",
  processing: "bg-purple-100 text-purple-800",
  shipped: "bg-indigo-100 text-indigo-800",
  delivered: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
  return_requested: "bg-orange-100 text-orange-800",
  returned: "bg-gray-100 text-gray-600",
};

const STEPS = ["paid", "processing", "shipped", "delivered"];

export default function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [returnForm, setReturnForm] = useState({ reason: "", description: "" });
  const [showReturnForm, setShowReturnForm] = useState(false);
  const [returning, setReturning] = useState(false);

  useEffect(() => {
    if (!id) return;
    ordersAPI.get(id).then((res) => setOrder(res.data)).finally(() => setLoading(false));
  }, [id]);

  const handleReturn = async (e: React.FormEvent) => {
    e.preventDefault();
    setReturning(true);
    try {
      await returnsAPI.create({ order_id: id, ...returnForm });
      toast.success("Return request submitted!");
      setShowReturnForm(false);
      const res = await ordersAPI.get(id!);
      setOrder(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setReturning(false);
    }
  };

  if (loading) return <LoadingSpinner className="py-20" size="lg" />;
  if (!order) return <div className="text-center py-20 text-gray-500">Order not found</div>;

  const currentStep = STEPS.indexOf(order.status);
  const canReturn = order.status === "delivered";

  return (
    <>
      <Helmet><title>Order #{order.order_number} – Kushi Fashions</title></Helmet>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button onClick={() => navigate("/orders")} className="flex items-center gap-1 text-gray-500 hover:text-gray-700 mb-6 text-sm">
          <ChevronLeftIcon className="h-4 w-4" /> My Orders
        </button>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">#{order.order_number}</h1>
            <p className="text-gray-500 text-sm">{format(new Date(order.created_at), "dd MMM yyyy, h:mm a")}</p>
          </div>
          <span className={`badge text-sm px-3 py-1 ${STATUS_COLORS[order.status] || "bg-gray-100"}`}>
            {order.status.replace("_", " ")}
          </span>
        </div>

        {/* Progress tracker */}
        {currentStep >= 0 && (
          <div className="card p-5 mb-6">
            <div className="flex items-center justify-between">
              {STEPS.map((step, i) => (
                <React.Fragment key={step}>
                  <div className="flex flex-col items-center">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      i <= currentStep ? "bg-primary-600 text-white" : "bg-gray-200 text-gray-400"
                    }`}>
                      {i < currentStep ? "✓" : i + 1}
                    </div>
                    <p className={`text-xs mt-1 capitalize ${i <= currentStep ? "text-primary-600 font-medium" : "text-gray-400"}`}>
                      {step}
                    </p>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 ${i < currentStep ? "bg-primary-600" : "bg-gray-200"}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-5">
            {/* Items */}
            <div className="card p-5">
              <h2 className="font-bold text-gray-900 mb-4">Order Items</h2>
              <div className="space-y-3">
                {order.items?.map((item: any) => (
                  <div key={item.id} className="flex gap-4 py-3 border-b border-gray-50 last:border-0">
                    <img
                      src={item.product_image || "https://via.placeholder.com/80/f9a8d4/ec4899?text=D"}
                      alt={item.product_name}
                      className="w-16 h-20 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">{item.product_name}</p>
                      <p className="text-sm text-gray-500">Size: {item.size} × {item.quantity}</p>
                      <p className="text-primary-600 font-medium mt-1">₹{Number(item.subtotal).toLocaleString("en-IN")}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Shipping address */}
            {order.shipping_address && (
              <div className="card p-5">
                <h2 className="font-bold text-gray-900 mb-3">Delivery Address</h2>
                <div className="text-sm text-gray-600 space-y-1">
                  <p className="font-medium">{order.shipping_address.full_name}</p>
                  <p>{order.shipping_address.address_line1}</p>
                  {order.shipping_address.address_line2 && <p>{order.shipping_address.address_line2}</p>}
                  <p>{order.shipping_address.city}, {order.shipping_address.state} – {order.shipping_address.pincode}</p>
                  <p>📞 {order.shipping_address.phone}</p>
                </div>
              </div>
            )}

            {/* Return form */}
            {canReturn && (
              <div className="card p-5">
                <h2 className="font-bold text-gray-900 mb-3">Request Return</h2>
                {showReturnForm ? (
                  <form onSubmit={handleReturn} className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
                      <select value={returnForm.reason} onChange={(e) => setReturnForm(f => ({ ...f, reason: e.target.value }))} required className="input-field">
                        <option value="">Select reason</option>
                        <option>Wrong size received</option>
                        <option>Product damaged</option>
                        <option>Not as described</option>
                        <option>Changed my mind</option>
                        <option>Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Details</label>
                      <textarea value={returnForm.description} onChange={(e) => setReturnForm(f => ({ ...f, description: e.target.value }))} rows={3} className="input-field resize-none" placeholder="Additional details..." />
                    </div>
                    <div className="flex gap-3">
                      <button type="submit" disabled={returning} className="btn-primary py-2 px-6">{returning ? "Submitting..." : "Submit Return"}</button>
                      <button type="button" onClick={() => setShowReturnForm(false)} className="btn-secondary py-2 px-6">Cancel</button>
                    </div>
                  </form>
                ) : (
                  <button onClick={() => setShowReturnForm(true)} className="btn-secondary py-2 px-6">Request Return</button>
                )}
              </div>
            )}
          </div>

          {/* Order summary */}
          <div className="card p-5 h-fit">
            <h2 className="font-bold text-gray-900 mb-4">Payment Summary</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <span>₹{Number(order.subtotal).toLocaleString("en-IN")}</span>
              </div>
              {Number(order.discount) > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount {order.coupon_code ? `(${order.coupon_code})` : ""}</span>
                  <span>-₹{Number(order.discount).toLocaleString("en-IN")}</span>
                </div>
              )}
              <div className="flex justify-between text-gray-600">
                <span>Shipping</span>
                <span>{Number(order.shipping_charge) === 0 ? "FREE" : `₹${order.shipping_charge}`}</span>
              </div>
              <hr />
              <div className="flex justify-between font-bold text-base">
                <span>Total</span>
                <span className="text-primary-600">₹{Number(order.total_amount).toLocaleString("en-IN")}</span>
              </div>
            </div>
            {order.tracking_number && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-blue-700">Tracking Info</p>
                <p className="text-xs text-blue-600 mt-1">{order.tracking_number}</p>
                {order.shipping_provider && <p className="text-xs text-blue-500">via {order.shipping_provider}</p>}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
